import type { DspModuleDefinition, DspModuleInstance, DspProcessBlock } from '../../dsp-module-contract/src/index.js';
import { validatePatchGraph, type PatchEndpoint, type PatchGraphV1 } from '../../routing-graph/src/index.js';

export class ModuleRegistry {
  private readonly definitions = new Map<string, DspModuleDefinition>();
  constructor(definitions: readonly DspModuleDefinition[] = []) { for (const definition of definitions) this.register(definition); }
  register(definition: DspModuleDefinition): void {
    const id = definition.descriptor.id;
    if (this.definitions.has(id)) throw new RangeError(`module already registered: ${id}`);
    this.definitions.set(id, definition);
  }
  get(id: string): DspModuleDefinition {
    const result = this.definitions.get(id);
    if (!result) throw new RangeError(`module is not registered: ${id}`);
    return result;
  }
  moduleIds(): readonly string[] { return Object.freeze([...this.definitions.keys()].sort()); }
  descriptors() { return this.moduleIds().map((id) => this.get(id).descriptor); }
}

interface RuntimeNode {
  readonly instance: DspModuleInstance;
  readonly block: { frames: number; inputs: Record<string, Float32Array>; outputs: Record<string, Float32Array>; controls: Record<string, Float32Array> };
}

export interface RuntimeContext { readonly sampleRate: number; readonly maxBlockSize: number }

export class PatchRuntime {
  private readonly graph: PatchGraphV1;
  private readonly order: readonly string[];
  private readonly nodes = new Map<string, RuntimeNode>();
  private readonly maxBlockSize: number;

  constructor(graph: PatchGraphV1, registry: ModuleRegistry, context: RuntimeContext) {
    if (!(context.sampleRate > 0) || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) throw new RangeError('invalid runtime context');
    const validated = validatePatchGraph(graph, registry.descriptors());
    // Own a snapshot: editing the caller's graph cannot silently rewire a live runtime.
    this.graph = structuredClone(graph); this.order = validated.executionOrder; this.maxBlockSize = context.maxBlockSize;
    for (const node of graph.nodes) {
      const definition = registry.get(node.moduleId);
      const outputs: Record<string, Float32Array> = {};
      for (const descriptor of definition.descriptor.outputs) outputs[descriptor.id] = new Float32Array(context.maxBlockSize * descriptor.channels);
      const block = { frames: 0, inputs: {} as Record<string, Float32Array>, outputs, controls: {} as Record<string, Float32Array> };
      // Unconnected audio means silence; absent optional controls retain module defaults.
      for (const descriptor of definition.descriptor.inputs) {
        if (descriptor.kind === 'audio') block.inputs[descriptor.id] = new Float32Array(context.maxBlockSize * descriptor.channels);
        else if (['trigger', 'gate', 'reset'].includes(descriptor.id)) block.controls[descriptor.id] = new Float32Array(context.maxBlockSize * descriptor.channels);
      }
      const instance = definition.createInstance(context);
      for (const parameter of definition.descriptor.parameters) instance.setParameter(parameter.id, node.parameters[parameter.id] ?? parameter.defaultValue);
      instance.reset(); // Initial snapshot parameters are not live automation.
      this.nodes.set(node.id, { instance, block });
    }
    for (const connection of graph.connections) {
      const source = this.nodes.get(connection.source.nodeId)!;
      const target = this.nodes.get(connection.target.nodeId)!;
      const buffer = source.block.outputs[connection.source.portId]!;
      const targetDescriptor = registry.get(graph.nodes.find((node) => node.id === connection.target.nodeId)!.moduleId).descriptor;
      const targetPort = targetDescriptor.inputs.find((port) => port.id === connection.target.portId)!;
      (targetPort.kind === 'control' ? target.block.controls : target.block.inputs)[connection.target.portId] = buffer;
    }
  }

  reset(): void { for (const id of this.order) this.nodes.get(id)!.instance.reset(); }

  process(frames: number, output: PatchEndpoint): Float32Array {
    if (!Number.isInteger(frames) || frames < 0 || frames > this.maxBlockSize) throw new RangeError('frames exceeds maximum block size');
    const buffer = this.nodes.get(output.nodeId)?.block.outputs[output.portId];
    if (!buffer) throw new RangeError('requested output does not exist');
    if (frames === 0) return buffer;
    // A port tap executes only its dependency closure, never parked/unrelated generators.
    const needed = new Set([output.nodeId]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const edge of this.graph.connections) if (needed.has(edge.target.nodeId) && !needed.has(edge.source.nodeId)) {
        needed.add(edge.source.nodeId); changed = true;
      }
    }
    for (const id of this.order) {
      if (!needed.has(id)) continue;
      const node = this.nodes.get(id)!;
      node.block.frames = frames;
      for (const values of Object.values(node.block.outputs)) values.fill(0);
      node.instance.process(node.block as DspProcessBlock);
    }
    return buffer;
  }

  topology() {
    return Object.freeze({
      schemaVersion: this.graph.schemaVersion,
      patchId: this.graph.id,
      executionOrder: Object.freeze([...this.order]),
      connections: Object.freeze(this.graph.connections.map((connection) => connection.id).sort()),
    });
  }
}
